// src/editor-overlay.js
// Editor overlay for visual element selection

import { getParentOrigin } from './parent-origin.js';
import { debugLog } from './debug-logger.js';

let currentHighlight = null;
let overlayActive = false;
let overlayElement = null;
let isPreviewMode = false;
let scopeHighlightedElements = new Set();
let allowedPreviewHostname = null;
let paramCaptureModeActive = false;
let paramCaptureHighlightedElement = null;
let paramCaptureHoverListener = null;
let paramCaptureClickListener = null;
let paramCaptureHoverTimeout = null;
let lastHoveredSelector = null;
let lastHoveredValue = null;
const INTERACTIVE_SELECTOR = 'a[href], button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"]';
const BLOCKED_INPUT_TYPES = new Set(['text', 'email', 'password', 'number', 'search', 'tel', 'url', 'file', 'range', 'color', 'date', 'datetime-local', 'month', 'time', 'week', 'hidden']);

function isBlockedElement(element) {
  const tag = element.tagName.toLowerCase();
  if (tag === 'textarea' || tag === 'select' || tag === 'label') return true;
  if (tag === 'input') {
    const type = (element.getAttribute('type') || 'text').toLowerCase();
    return BLOCKED_INPUT_TYPES.has(type);
  }
  return false;
}
const PARAM_CAPTURE_PREVENT_SELECTOR = 'a, button[type="submit"], input[type="submit"], input[type="reset"], form';
const PARAM_CAPTURE_MAX_DISPLAY_LENGTH = 120;
const PARAM_CAPTURE_MAX_CONTAINER_TEXT_LENGTH = 120;
const PARAM_CAPTURE_MAX_DESCENDANT_TEXT_LENGTH = 80;
const PARAM_CAPTURE_MAX_DESCENDANT_NODES = 50;
const PARAM_CAPTURE_MAX_DATA_ATTRIBUTE_LENGTH = 60;
const PARAM_CAPTURE_MEANINGFUL_SELECTOR = 'button, a, h1, h2, h3, label, [aria-label], [alt], input, select, textarea';

/**
 * Check if editor mode is currently active
 * Used by tracker to prevent events from firing during editing
 * @returns {boolean} True if editor overlay is active
 */
export function isEditorActive() {
  return overlayActive;
}

/**
 * Check if preview mode is currently active
 * Used by tracker to send event logs to parent
 * @returns {boolean} True if preview mode is active
 */
export function isPreviewActive() {
  return isPreviewMode;
}

/**
 * Get allowed parent origins based on environment
 */
function getAllowedOrigins() {
  return [
    'http://localhost',
    'https://eventfoundry.app'
  ];
}

/**
 * Validate that postMessage came from allowed origin
 * @param {string} origin - The origin from the message event
 * @returns {boolean} True if origin is allowed
 */
function isOriginAllowed(origin) {
  const allowed = getAllowedOrigins();

  // Check for exact match or localhost with any port
  return allowed.some(allowedOrigin => {
    if (allowedOrigin === 'http://localhost') {
      return origin.startsWith('http://localhost');
    }
    return origin === allowedOrigin;
  });
}

/**
 * Remove scope highlight classes from all previously highlighted elements
 */
function clearScopeHighlights() {
  scopeHighlightedElements.forEach(el => {
    el.classList.remove('eventfoundry-scope-highlight');
    el.classList.remove('eventfoundry-scope-highlight--static');
    el.classList.remove('eventfoundry-scope-highlight--selected');
  });
  scopeHighlightedElements.clear();
}

function normalizePreviewHostname(hostname) {
  if (!hostname) {
    return '';
  }

  return hostname.replace(/^www\./, '').toLowerCase();
}

function getPreviewNavigationLink(target) {
  if (!target || !(target instanceof Element)) {
    return null;
  }

  return target.closest('a[href]');
}

function isAllowedPreviewNavigation(anchor) {
  if (!anchor) {
    return true;
  }

  const href = anchor.getAttribute('href');
  if (!href) {
    return true;
  }

  let nextUrl;
  try {
    nextUrl = new URL(href, window.location.href);
  } catch {
    return false;
  }

  if (nextUrl.protocol !== 'http:' && nextUrl.protocol !== 'https:') {
    return false;
  }

  return normalizePreviewHostname(nextUrl.hostname) === allowedPreviewHostname;
}

/**
 * Create and activate editor overlay
 */
export function createEditorOverlay(debugMode = false) {
  if (overlayActive) {
    return;
  }

  overlayActive = true;
  allowedPreviewHostname = normalizePreviewHostname(window.location.hostname);

  // Add overlay styles
  const style = document.createElement('style');
  style.textContent = `
    .eventfoundry-highlight {
      outline: 2px solid #6366f1 !important;
      outline-offset: 2px;
      cursor: pointer !important;
    }
    @keyframes efScopePulse {
      0%   { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.7); }
      100% { box-shadow: 0 0 0 8px rgba(99, 102, 241, 0.2); }
    }
    .eventfoundry-scope-highlight {
      outline: 2px solid rgba(99, 102, 241, 0.25) !important;
      outline-offset: 2px;
      animation: efScopePulse 0.6s ease-out 1 forwards !important;
    }
    .eventfoundry-scope-highlight--static {
      outline: 2px solid #6366f1 !important;
      outline-offset: 2px;
    }
    @keyframes efScopePulseSelected {
      0%   { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.9); }
      100% { box-shadow: 0 0 0 10px rgba(99, 102, 241, 0.35); }
    }
    .eventfoundry-scope-highlight--selected {
      outline: 2px solid rgba(99, 102, 241, 0.5) !important;
      outline-offset: 3px;
      animation: efScopePulseSelected 0.6s ease-out 1 forwards !important;
    }
    .eventfoundry-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 999998;
      pointer-events: none;
      cursor: crosshair;
    }
    body.eventfoundry-edit-mode,
    body.eventfoundry-edit-mode * {
      cursor: crosshair !important;
    }
  `;
  document.head.appendChild(style);

  // Create overlay div (for visual indication - crosshair cursor)
  const overlay = document.createElement('div');
  overlay.className = 'eventfoundry-overlay';
  document.body.appendChild(overlay);

  // Store overlay element reference for show/hide
  overlayElement = overlay;

  // Add edit mode class to body for crosshair cursor
  document.body.classList.add('eventfoundry-edit-mode');

  // Mouseover - highlight element
  // Use document.body with capture phase to intercept all mouseover events
  document.body.addEventListener('mouseover', (e) => {
    // Skip highlighting in Preview Mode
    if (isPreviewMode || paramCaptureModeActive) {
      return;
    }

    // Ignore the overlay itself and EventFoundry-specific elements
    if (e.target.classList.contains('eventfoundry-overlay')) return;

    const selectableElement = getSelectableElement(e.target);
    if (!selectableElement) return;

    // Remove previous highlight
    if (currentHighlight) {
      currentHighlight.classList.remove('eventfoundry-highlight');
    }

    // Add highlight to hovered (or nearest selectable) element
    selectableElement.classList.add('eventfoundry-highlight');
    currentHighlight = selectableElement;
  }, true);

  // Mouseout - remove highlight
  document.body.addEventListener('mouseout', (e) => {
    // Skip in Preview Mode
    if (isPreviewMode || paramCaptureModeActive) {
      return;
    }

    // Ignore the overlay itself
    if (e.target.classList.contains('eventfoundry-overlay')) return;

    const selectableElement = getSelectableElement(e.target);
    if (selectableElement) {
      selectableElement.classList.remove('eventfoundry-highlight');
    }
  }, true);

  // Click - select element
  document.body.addEventListener('click', (e) => {
    // Ignore the overlay itself
    if (e.target.classList.contains('eventfoundry-overlay')) {
      return;
    }

    if (paramCaptureModeActive) {
      return;
    }

    // If in Preview Mode, let click pass through normally
    if (isPreviewMode) {
      const previewLink = getPreviewNavigationLink(e.target);
      if (previewLink) {
        if (!isAllowedPreviewNavigation(previewLink)) {
          e.preventDefault();
          e.stopPropagation();
          const targetOrigin = getParentOrigin();
          if (targetOrigin) {
            window.parent.postMessage({ type: 'NAVIGATION_BLOCKED', href: previewLink.href }, targetOrigin);
          }
        } else if (previewLink.getAttribute('target') === '_blank') {
          // Same-domain _blank link: navigate within the iframe instead of opening a new tab
          e.preventDefault();
          const href = previewLink.getAttribute('href');
          if (href) {
            window.location.href = href;
          }
        }
      }

      return; // Don't prevent default for normal links, don't select element
    }

    // Edit Mode: Intercept click for element selection
    e.preventDefault();
    e.stopPropagation();

    // Resolve to nearest clickable/interactive ancestor for better tracking targets
    const element = getSelectableElement(e.target);
    if (!element) {
      return;
    }

    // Filter out EventFoundry classes from className
    let cleanClassName = '';
    if (element.className && typeof element.className === 'string') {
      cleanClassName = element.className
        .trim()
        .split(/\s+/)
        .filter(cls => !cls.startsWith('eventfoundry-'))
        .join(' ');
    }

    // Extract page path (everything after domain)
    const pagePath = window.location.pathname + window.location.search + window.location.hash;

    // For void elements like <input>, textContent is always empty.
    // Fall back to the value attribute (e.g. input[type="submit"] value="Get a Quote").
    const rawText = element.textContent.trim().substring(0, 50)
      || (element.tagName === 'INPUT' && typeof element.value === 'string' ? element.value.substring(0, 50) : '');

    const elementData = {
      text: rawText,
      className: cleanClassName,
      selector: computeSelector(element),
      pagePath: pagePath
    };

    // Send to parent window (Vue app) using the verified parent origin
    const targetOrigin = getParentOrigin();
    if (targetOrigin) {
      const elementSelectedPayload = { type: 'ELEMENT_SELECTED', element: elementData };
      window.parent.postMessage(elementSelectedPayload, targetOrigin);
      debugLog(debugMode, '→ ELEMENT_SELECTED sent to parent', elementSelectedPayload);
    } else {
      console.error('EventFoundry: Cannot send ELEMENT_SELECTED - parent origin not established');
    }
  }, true);

  // Listen for "count matches" and scope highlight messages from parent
  window.addEventListener('message', (event) => {
    // Validate origin
    if (!isOriginAllowed(event.origin)) {
      return; // Silently ignore (already logged in editor-mode.js)
    }

    // Handle COUNT_MATCHES message
    if (event.data.type === 'COUNT_MATCHES') {
      const selector = event.data.selector;
      let count = 0;

      try {
        count = document.querySelectorAll(selector).length;
      } catch (e) {
        // Invalid selector, count stays 0
        count = 0;
      }

      // Send count back to parent using the verified parent origin
      const targetOrigin = getParentOrigin();
      if (targetOrigin) {
        window.parent.postMessage({
          type: 'MATCH_COUNT_RESULT',
          selector: selector,
          count: count
        }, targetOrigin);
      }
    }

    if (event.data.type === 'HIGHLIGHT_SCOPE') {
      clearScopeHighlights();
      const matches = document.querySelectorAll(event.data.selector);
      if (matches.length === 0) return;
      const isLarge = matches.length > 100;
      requestAnimationFrame(() => {
        matches.forEach(el => {
          let cls;
          try {
            if (event.data.selectedSelector && el.matches(event.data.selectedSelector)) {
              cls = 'eventfoundry-scope-highlight--selected';
            } else if (isLarge) {
              cls = 'eventfoundry-scope-highlight--static';
            } else {
              cls = 'eventfoundry-scope-highlight';
            }
          } catch {
            cls = isLarge ? 'eventfoundry-scope-highlight--static' : 'eventfoundry-scope-highlight';
          }
          el.classList.add(cls);
          scopeHighlightedElements.add(el);
        });
      });
    }

    if (event.data.type === 'CLEAR_HIGHLIGHT') {
      clearScopeHighlights();
    }
  });
}

function ensureParamCaptureStyles() {
  if (document.getElementById('eventfoundry-param-capture-styles')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'eventfoundry-param-capture-styles';
  style.textContent = `
    .eventfoundry-param-capture-highlight {
      outline: 2px solid #D97706 !important;
      outline-offset: 2px;
    }
  `;
  document.head.appendChild(style);
}

function clearParamCaptureHighlight() {
  if (!paramCaptureHighlightedElement) {
    return;
  }

  paramCaptureHighlightedElement.classList.remove('eventfoundry-param-capture-highlight');
  paramCaptureHighlightedElement = null;
}

function getParamCaptureTarget(target) {
  if (!(target instanceof Element)) {
    return null;
  }

  if (target === document.documentElement || target === document.body) {
    return null;
  }

  return target;
}

function normalizePreviewText(value) {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/\s+/g, ' ').trim();
}

function clampPreviewText(value, maxLength = PARAM_CAPTURE_MAX_DISPLAY_LENGTH) {
  if (!value || value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
}

function getShortSelectorHint(element) {
  if (!element || !(element instanceof Element)) {
    return '';
  }

  const id = element.getAttribute('id');
  if (id && !isDynamicId(id)) {
    return `#${id}`;
  }

  const classes = getCleanClasses(element);
  if (classes.length > 0) {
    return `.${classes[0]}`;
  }

  return element.tagName.toLowerCase();
}

function getShortDataAttribute(element) {
  const attributeNames = element.getAttributeNames();

  for (const name of attributeNames) {
    if (!name.startsWith('data-') || name === 'data-eventfoundry-ignore') {
      continue;
    }

    const value = normalizePreviewText(element.getAttribute(name) || '');
    if (value && value.length <= PARAM_CAPTURE_MAX_DATA_ATTRIBUTE_LENGTH) {
      return { name, value };
    }
  }

  return null;
}

function hasMeaningfulText(text, maxLength) {
  return !!text && text.length <= maxLength;
}

function findMeaningfulDescendant(root) {
  const queue = Array.from(root.children);
  let visited = 0;
  let fallback = null;

  while (queue.length > 0 && visited < PARAM_CAPTURE_MAX_DESCENDANT_NODES) {
    const current = queue.shift();
    visited += 1;

    if (!(current instanceof Element)) {
      continue;
    }

    if (
      current.matches(PARAM_CAPTURE_MEANINGFUL_SELECTOR) ||
      current.getAttributeNames().some(name => name.startsWith('data-'))
    ) {
      return current;
    }

    const text = normalizePreviewText(current.textContent || '');
    if (!fallback && hasMeaningfulText(text, PARAM_CAPTURE_MAX_DESCENDANT_TEXT_LENGTH)) {
      fallback = current;
    }

    queue.push(...current.children);
  }

  return fallback;
}

function getHoverPreviewValue(targetEl, allowDescendantFallback = true) {
  if (!(targetEl instanceof Element)) {
    return { value: '', sourceHint: '', resolvedElement: null };
  }

  const formValue = 'value' in targetEl ? normalizePreviewText(targetEl.value) : '';
  if (formValue && targetEl.matches('input, select, textarea')) {
    return {
      value: clampPreviewText(formValue),
      sourceHint: 'field value',
      resolvedElement: targetEl,
    };
  }

  for (const [attribute, label] of [['aria-label', 'attribute: aria-label'], ['alt', 'attribute: alt'], ['title', 'attribute: title']]) {
    const attributeValue = normalizePreviewText(targetEl.getAttribute(attribute) || '');
    if (attributeValue) {
      return {
        value: clampPreviewText(attributeValue),
        sourceHint: label,
        resolvedElement: targetEl,
      };
    }
  }

  const dataAttribute = getShortDataAttribute(targetEl);
  if (dataAttribute) {
    return {
      value: clampPreviewText(dataAttribute.value),
      sourceHint: `attribute: ${dataAttribute.name}`,
      resolvedElement: targetEl,
    };
  }

  const rawText = normalizePreviewText(targetEl.textContent || '');
  const looksLikeContainer = !hasMeaningfulText(rawText, PARAM_CAPTURE_MAX_CONTAINER_TEXT_LENGTH) || targetEl.children.length > 3;

  if (allowDescendantFallback && looksLikeContainer) {
    const meaningfulDescendant = findMeaningfulDescendant(targetEl);
    if (meaningfulDescendant) {
      const descendantPreview = getHoverPreviewValue(meaningfulDescendant, false);
      if (descendantPreview.value) {
        return {
          value: descendantPreview.value,
          sourceHint: descendantPreview.sourceHint || getShortSelectorHint(meaningfulDescendant),
          resolvedElement: meaningfulDescendant,
        };
      }
    }
  }

  return {
    value: clampPreviewText(rawText),
    sourceHint: getShortSelectorHint(targetEl),
    resolvedElement: targetEl,
  };
}

export function enterParamCaptureMode(parentOrigin) {
  if (paramCaptureModeActive) {
    return;
  }

  paramCaptureModeActive = true;
  ensureParamCaptureStyles();

  document.body.style.cursor = 'crosshair';

  paramCaptureHoverListener = (e) => {
    if (!paramCaptureModeActive) {
      return;
    }

    const target = getParamCaptureTarget(e.target);
    if (!target) return;

    clearParamCaptureHighlight();
    target.classList.add('eventfoundry-param-capture-highlight');
    paramCaptureHighlightedElement = target;

    const preview = getHoverPreviewValue(target);
    const hoveredElement = preview.resolvedElement || target;
    const selector = computeSelector(hoveredElement);
    const value = preview.value;
    if (selector === lastHoveredSelector && value === lastHoveredValue) {
      return;
    }

    if (paramCaptureHoverTimeout) {
      clearTimeout(paramCaptureHoverTimeout);
    }

    paramCaptureHoverTimeout = setTimeout(() => {
      if (!paramCaptureModeActive) {
        return;
      }

      lastHoveredSelector = selector;
      lastHoveredValue = value;
      window.parent.postMessage({
        type: 'PARAM_VALUE_HOVERED',
        selector,
        value,
        sourceHint: preview.sourceHint || getShortSelectorHint(hoveredElement)
      }, parentOrigin);
    }, 50);
  };

  paramCaptureClickListener = (e) => {
    if (!paramCaptureModeActive) {
      return;
    }

    const target = getParamCaptureTarget(e.target);
    if (!target) return;

    e.stopPropagation();

    if (target.closest(PARAM_CAPTURE_PREVENT_SELECTOR)) {
      e.preventDefault();
    }

    const preview = getHoverPreviewValue(target);
    const selectedElement = preview.resolvedElement || target;

    window.parent.postMessage({
      type: 'PARAM_ELEMENT_SELECTED',
      selector: computeSelector(selectedElement),
      attribute: 'textContent',
      value: preview.value,
      sourceHint: preview.sourceHint || getShortSelectorHint(selectedElement)
    }, parentOrigin);
  };

  document.addEventListener('mouseover', paramCaptureHoverListener, true);
  document.addEventListener('click', paramCaptureClickListener, true);
}

export function exitParamCaptureMode() {
  if (!paramCaptureModeActive) {
    return;
  }

  paramCaptureModeActive = false;

  document.body.style.cursor = '';

  if (paramCaptureHoverListener) {
    document.removeEventListener('mouseover', paramCaptureHoverListener, true);
    paramCaptureHoverListener = null;
  }

  if (paramCaptureClickListener) {
    document.removeEventListener('click', paramCaptureClickListener, true);
    paramCaptureClickListener = null;
  }

  if (paramCaptureHoverTimeout) {
    clearTimeout(paramCaptureHoverTimeout);
    paramCaptureHoverTimeout = null;
  }

  clearParamCaptureHighlight();
  lastHoveredSelector = null;
  lastHoveredValue = null;
}

/**
 * Find the element that should be selected for tracking.
 * Prefers semantic interactive elements, then common clickable fallbacks.
 *
 * @param {Element} target - The original event target
 * @returns {Element|null} Best selectable element
 */
function getSelectableElement(target) {
  if (!target || !(target instanceof Element)) {
    return null;
  }

  const semanticInteractive = target.closest(INTERACTIVE_SELECTOR);
  if (semanticInteractive) {
    return isBlockedElement(semanticInteractive) ? null : semanticInteractive;
  }

  let current = target;
  while (current && current !== document.body) {
    if (current.hasAttribute('onclick')) {
      return isBlockedElement(current) ? null : current;
    }

    // Only treat tabindex as a signal when the element is explicitly marked as a button.
    // A bare tabindex="0" is often added to structural containers (sections, scroll regions)
    // for accessibility, not because they are clickable action targets.
    const tabindex = current.getAttribute('tabindex');
    const role = current.getAttribute('role');
    if (tabindex && tabindex !== '-1' && role === 'button') {
      return isBlockedElement(current) ? null : current;
    }

    current = current.parentElement;
  }

  return isBlockedElement(target) ? null : target;
}

/**
 * Escape special characters in CSS selectors
 * Handles: . : [ ] # ( ) , > + ~ @ ! $ % ^ & * = | \ / ' "
 */
function escapeSelector(str) {
  if (!str) return '';
  // Escape special CSS characters
  return str.replace(/([!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~])/g, '\\$1');
}

/**
 * Check if an ID looks dynamically generated (UUID, timestamp, random hash)
 * Dynamic IDs are unreliable for tracking as they change on page reloads
 */
function isDynamicId(id) {
  if (!id) return false;

  // Check for UUID patterns (e.g., "abc-123-def-456")
  const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
  if (uuidPattern.test(id)) return true;

  // Check for timestamp-like patterns (13 digit numbers)
  const timestampPattern = /\d{13}/;
  if (timestampPattern.test(id)) return true;

  // Check for random hash patterns (long hex strings)
  const hashPattern = /^[a-f0-9]{16,}$/i;
  if (hashPattern.test(id)) return true;

  // Check for IDs that end with random numbers (e.g., "button-12345")
  const randomSuffixPattern = /-\d{4,}$/;
  if (randomSuffixPattern.test(id)) return true;

  // CMS/build-system generated IDs (common in Squarespace and similar templates)
  const cmsGeneratedPattern = /^(block-yui_|block-[a-f0-9]{8,}|page-[a-f0-9]{8,}|collection-[a-f0-9]{8,}|shopify-section-)|template--/i;
  if (cmsGeneratedPattern.test(id)) return true;

  return false;
}

/**
 * Validate that a selector uniquely identifies the target element
 * @param {Element} element - The target element
 * @param {string} selector - The CSS selector to validate
 * @returns {boolean} True if selector returns exactly 1 element and it matches the target
 */
function validateSelectorUniqueness(element, selector) {
  try {
    const matches = document.querySelectorAll(selector);
    // Must match exactly 1 element, and it must be our target element
    return matches.length === 1 && matches[0] === element;
  } catch (e) {
    // Invalid selector
    return false;
  }
}

/**
 * Get element's classes, filtering out EventFoundry internal classes
 * @param {Element} element - The target element
 * @returns {string[]} Array of clean class names
 */
function getCleanClasses(element) {
  if (!element.className || typeof element.className !== 'string') {
    return [];
  }

  return element.className
    .trim()
    .split(/\s+/)
    .filter(cls => cls && !cls.startsWith('eventfoundry-'));
}

/**
 * Get a selector for the element's parent
 * @param {Element} element - The child element
 * @param {boolean} useFirstClassOnly - If true, use only first class; otherwise use tag name
 * @returns {string|null} Parent selector or null if no parent
 */
function getParentSelector(element, useFirstClassOnly = true) {
  const parent = element.parentElement;
  if (!parent) {
    return null;
  }

  const parentClasses = getCleanClasses(parent);

  if (useFirstClassOnly && parentClasses.length > 0) {
    return `.${escapeSelector(parentClasses[0])}`;
  }

  return parent.tagName.toLowerCase();
}

/**
 * Try to add a selector to the array if it uniquely identifies the element
 * @param {Array} selectors - Array to add selector to
 * @param {string} selector - The CSS selector to try
 * @param {number} priority - Priority score for this selector
 * @param {Element} element - The target element
 * @returns {boolean} True if selector was added (was unique)
 */
function tryAddSelector(selectors, selector, priority, element) {
  if (validateSelectorUniqueness(element, selector)) {
    selectors.push({ selector, priority });
    return true;
  }
  return false;
}

/**
 * Generate ID-based selectors
 * Priority: 100 (highest - IDs should be unique)
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: '#submit-btn', priority: 100 }]
 */
function generateIdSelectors(element) {
  const selectors = [];

  // Only use non-dynamic IDs
  // Use getAttribute('id') instead of .id to avoid the named form element collision:
  // Shopify forms contain <input name="id">, which shadows form.id with the input element
  const elementId = element.getAttribute('id');
  if (elementId && !isDynamicId(elementId)) {
    const idSelector = `#${escapeSelector(elementId)}`;
    tryAddSelector(selectors, idSelector, 100, element);
  }

  return selectors;
}

/**
 * Generate data attribute selectors
 * Priority: 90 (high - data attributes often used for tracking)
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: '[data-track="signup"]', priority: 90 }]
 */
function generateDataAttrSelectors(element) {
  const selectors = [];

  const dataAttrs = Array.from(element.attributes)
    .filter(attr => attr.name.startsWith('data-') && attr.value);

  // Try each data attribute individually
  for (const attr of dataAttrs) {
    const attrSelector = `[${attr.name}="${escapeSelector(attr.value)}"]`;
    if (tryAddSelector(selectors, attrSelector, 90, element)) {
      break; // Use first unique data attribute found
    }
  }

  return selectors;
}

/**
 * Generate selectors for common clickable elements and semantics.
 * Priority: 86-96 (high - specifically tuned for links/buttons)
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 */
function generateClickableSelectors(element) {
  const selectors = [];
  const tagName = element.tagName.toLowerCase();

  if (tagName === 'a') {
    const href = element.getAttribute('href');
    if (href && !href.startsWith('javascript:')) {
      tryAddSelector(selectors, `a[href="${escapeSelector(href)}"]`, 96, element);
    }

    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) {
      tryAddSelector(selectors, `a[aria-label="${escapeSelector(ariaLabel)}"]`, 92, element);
    }
  }

  if (tagName === 'button') {
    const buttonType = element.getAttribute('type');
    if (buttonType) {
      tryAddSelector(selectors, `button[type="${escapeSelector(buttonType)}"]`, 94, element);
    }

    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) {
      tryAddSelector(selectors, `button[aria-label="${escapeSelector(ariaLabel)}"]`, 92, element);
    }
  }

  const role = element.getAttribute('role');
  if (role === 'button') {
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) {
      tryAddSelector(selectors, `[role="button"][aria-label="${escapeSelector(ariaLabel)}"]`, 91, element);
    }
  }

  return selectors;
}

/**
 * Generate class-based selectors with improved specificity
 * Tries multiple strategies:
 * 1. Tag + single class (Priority: 85) - e.g., button.btn-primary
 * 2. Multi-class combinations (Priority: 82) - e.g., .btn.btn-primary
 * 3. Single class (Priority: 80) - e.g., .btn-primary
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [
 *   { selector: 'button.btn-primary', priority: 85 },
 *   { selector: '.btn.btn-primary', priority: 82 }
 * ]
 */
function generateClassSelectors(element) {
  const selectors = [];
  const classes = getCleanClasses(element);

  if (classes.length === 0) {
    return selectors;
  }

  const tagName = element.tagName.toLowerCase();

  // Strategy 1: Try tag + single class combinations (most specific)
  for (const cls of classes) {
    const tagClassSelector = `${tagName}.${escapeSelector(cls)}`;
    if (tryAddSelector(selectors, tagClassSelector, 85, element)) {
      break; // Found a unique tag+class combo
    }
  }

  // Strategy 2: Try multi-class combinations (2 classes)
  if (classes.length >= 2) {
    // Try combining first two classes
    const multiClassSelector = `.${escapeSelector(classes[0])}.${escapeSelector(classes[1])}`;
    tryAddSelector(selectors, multiClassSelector, 82, element);

    // If element has 3+ classes, try first three
    if (classes.length >= 3) {
      const tripleClassSelector = `.${escapeSelector(classes[0])}.${escapeSelector(classes[1])}.${escapeSelector(classes[2])}`;
      tryAddSelector(selectors, tripleClassSelector, 82, element);
    }
  }

  // Strategy 3: Try single classes (original behavior - fallback)
  for (const cls of classes) {
    const classSelector = `.${escapeSelector(cls)}`;
    if (tryAddSelector(selectors, classSelector, 80, element)) {
      break; // Use first unique class found
    }
  }

  return selectors;
}

/**
 * Generate semantic attribute selectors
 * Priority: 70 (medium - attributes like type, name, href can be meaningful)
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: 'button[type="submit"]', priority: 70 }]
 */
function generateAttributeSelectors(element) {
  const selectors = [];
  const tagName = element.tagName.toLowerCase();

  // Semantic attributes to check (in priority order)
  const semanticAttrs = ['type', 'name', 'href', 'role', 'aria-label'];

  for (const attrName of semanticAttrs) {
    const attrValue = element.getAttribute(attrName);
    if (attrValue) {
      // Try tag + attribute combination (more specific)
      const tagAttrSelector = `${tagName}[${attrName}="${escapeSelector(attrValue)}"]`;
      if (tryAddSelector(selectors, tagAttrSelector, 70, element)) {
        break; // Found unique semantic selector
      }
    }
  }

  return selectors;
}

/**
 * Generate selectors with parent context for improved specificity
 * Priority: 65-75 (medium-high - adds context to prevent collisions)
 *
 * Strategies:
 * 1. Parent class + child class (Priority: 75)
 * 2. Parent class + child tag+class (Priority: 72)
 * 3. Parent class + child tag (Priority: 65)
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: '.modal .btn-primary', priority: 75 }]
 */
function generateParentContextSelectors(element) {
  const selectors = [];
  const parentSelector = getParentSelector(element, true);

  if (!parentSelector) {
    return selectors; // No parent
  }

  const tagName = element.tagName.toLowerCase();
  const classes = getCleanClasses(element);

  // Strategy 1: Parent + child's first class
  if (classes.length > 0) {
    const parentChildClassSelector = `${parentSelector} .${escapeSelector(classes[0])}`;
    tryAddSelector(selectors, parentChildClassSelector, 75, element);
  }

  // Strategy 2: Parent + child tag+class
  if (classes.length > 0) {
    const parentTagClassSelector = `${parentSelector} ${tagName}.${escapeSelector(classes[0])}`;
    tryAddSelector(selectors, parentTagClassSelector, 72, element);
  }

  // Strategy 3: Parent + child tag (basic context)
  const parentTagSelector = `${parentSelector} ${tagName}`;
  tryAddSelector(selectors, parentTagSelector, 65, element);

  return selectors;
}

/**
 * Generate selectors using ancestor context with minimal positional hints.
 * Priority: 60-68 (between parent context and structural fallback)
 *
 * Goal: find shorter unique selectors than full DOM paths when classes repeat.
 * Example: .test-section:nth-of-type(2) .btn-danger
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 */
function generateAncestorContextSelectors(element) {
  const selectors = [];
  const tagName = element.tagName.toLowerCase();
  const classes = getCleanClasses(element);

  const childCandidates = [];
  if (classes.length > 0) {
    childCandidates.push(`.${escapeSelector(classes[0])}`);
    childCandidates.push(`${tagName}.${escapeSelector(classes[0])}`);
    if (classes.length >= 2) {
      childCandidates.push(`.${escapeSelector(classes[0])}.${escapeSelector(classes[1])}`);
    }
  }
  childCandidates.push(tagName);

  let ancestor = element.parentElement;
  while (ancestor && ancestor.tagName && ancestor.tagName.toLowerCase() !== 'body' && ancestor.tagName.toLowerCase() !== 'html') {
    const ancestorTag = ancestor.tagName.toLowerCase();
    const ancestorClasses = getCleanClasses(ancestor);
    const ancestorCandidates = [];

    const ancestorId = ancestor.getAttribute('id');
    if (ancestorId && !isDynamicId(ancestorId)) {
      ancestorCandidates.push({ selector: `#${escapeSelector(ancestorId)}`, priority: 68 });
    }

    if (ancestorClasses.length > 0) {
      ancestorCandidates.push({ selector: `.${escapeSelector(ancestorClasses[0])}`, priority: 66 });
      ancestorCandidates.push({
        selector: `${ancestorTag}.${escapeSelector(ancestorClasses[0])}`,
        priority: 65
      });

      const nthOfTypeIndex = Array.from(ancestor.parentElement.children)
        .filter(child => child.tagName === ancestor.tagName)
        .indexOf(ancestor) + 1;
      ancestorCandidates.push({
        selector: `${ancestorTag}.${escapeSelector(ancestorClasses[0])}:nth-of-type(${nthOfTypeIndex})`,
        priority: 63
      });
    }

    ancestorCandidates.push({ selector: ancestorTag, priority: 62 });

    for (const ancestorCandidate of ancestorCandidates) {
      for (const childCandidate of childCandidates) {
        const combined = `${ancestorCandidate.selector} ${childCandidate}`;
        if (tryAddSelector(selectors, combined, ancestorCandidate.priority, element)) {
          // Keep collecting; computeSelector will pick highest-priority unique option.
        }
      }
    }

    ancestor = ancestor.parentElement;
  }

  return selectors;
}

/**
 * Generate structural selector using nth-child
 * Priority: 50 (lowest - fragile, breaks when DOM order changes)
 *
 * This is the ultimate fallback when semantic selectors don't work.
 * Uses parent context to make it slightly more resilient.
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: '.container button:nth-child(3)', priority: 50 }]
 */
function generateStructuralSelector(element) {
  const selectors = [];
  const tagName = element.tagName.toLowerCase();
  const parent = element.parentElement;

  if (!parent) {
    // No parent - just return tag name
    return [{ selector: tagName, priority: 10 }];
  }

  // Build nth-child selector with parent context
  const parentSelector = getParentSelector(element, true);
  const siblings = Array.from(parent.children);
  const index = siblings.indexOf(element) + 1;
  const nthChildSelector = `${parentSelector} ${tagName}:nth-child(${index})`;

  tryAddSelector(selectors, nthChildSelector, 50, element);

  // Fallback: build full DOM path with nth-of-type segments for guaranteed uniqueness.
  // This is more resilient than a plain tag fallback and still valid CSS.
  if (selectors.length === 0) {
    const fullPathSelector = buildFullPathSelector(element);
    tryAddSelector(selectors, fullPathSelector, 45, element);
  }

  return selectors;
}

/**
 * Build a full DOM path selector using nth-of-type for each level.
 * Example: body > div:nth-of-type(1) > button:nth-of-type(3)
 *
 * @param {Element} element - The target element
 * @returns {string} Full path selector
 */
function buildFullPathSelector(element) {
  const segments = [];
  let current = element;

  while (current && current.nodeType === Node.ELEMENT_NODE) {
    const tagName = current.tagName.toLowerCase();
    const parent = current.parentElement;

    if (!parent) {
      segments.unshift(tagName);
      break;
    }

    const sameTagSiblings = Array.from(parent.children)
      .filter(child => child.tagName === current.tagName);
    const index = sameTagSiblings.indexOf(current) + 1;

    segments.unshift(`${tagName}:nth-of-type(${index})`);

    // Stop at body for readability and stability.
    if (parent.tagName && parent.tagName.toLowerCase() === 'body') {
      segments.unshift('body');
      break;
    }

    current = parent;
  }

  return segments.join(' > ');
}

/**
 * Compute CSS selector for element using priority scoring system
 *
 * Orchestrates multiple selector generation strategies and returns
 * the highest priority selector that uniquely identifies the element.
 *
 * Priority scoring (highest to lowest):
 * - ID (non-dynamic): 100
 * - Clickable semantics (href/type/aria-label): 86-96
 * - Data attributes: 90
 * - Tag + class: 85
 * - Multi-class: 82
 * - Single class: 80
 * - Parent + child class: 75
 * - Parent + tag+class: 72
 * - Semantic attributes: 70
 * - Parent + tag: 65
 * - Ancestor context: 60-68
 * - Structural (nth-child): 50
 * - Structural (full path nth-of-type): 45
 *
 * @param {Element} element - The target element
 * @returns {string} The best CSS selector for the element
 */
function computeSelector(element) {
  const selectors = [];

  // Collect selector candidates from all strategies
  selectors.push(...generateIdSelectors(element));
  selectors.push(...generateClickableSelectors(element));
  selectors.push(...generateDataAttrSelectors(element));
  selectors.push(...generateClassSelectors(element));
  selectors.push(...generateAttributeSelectors(element));
  selectors.push(...generateParentContextSelectors(element));
  selectors.push(...generateAncestorContextSelectors(element));
  selectors.push(...generateStructuralSelector(element));

  // Sort by priority (highest first) and return the best selector
  if (selectors.length > 0) {
    selectors.sort((a, b) => b.priority - a.priority);
    return selectors[0].selector;
  }

  // Ultimate fallback: just use tag name (will match multiple elements, but won't crash)
  return element.tagName.toLowerCase();
}

/**
 * Enable Preview Mode - hide overlay and allow normal interactions
 */
export function enablePreviewOverlay() {
  if (!overlayActive) {
    console.warn('EventFoundry: Cannot enable preview - overlay not active');
    return;
  }

  clearScopeHighlights();

  isPreviewMode = true;

  console.log('EventFoundry: Preview Mode enabled - all interactions allowed');

  // Hide overlay visually
  if (overlayElement) {
    overlayElement.style.display = 'none';
  }

  // Remove edit mode class to restore normal cursors
  document.body.classList.remove('eventfoundry-edit-mode');

  // Remove current highlight if any
  if (currentHighlight) {
    currentHighlight.classList.remove('eventfoundry-highlight');
    currentHighlight = null;
  }
}

/**
 * Disable Preview Mode - show overlay and enable element selection
 */
export function disablePreviewOverlay() {
  if (!overlayActive) {
    console.warn('EventFoundry: Cannot disable preview - overlay not active');
    return;
  }

  isPreviewMode = false;

  console.log('EventFoundry: Preview Mode disabled - Edit Mode active');

  // Show overlay again
  if (overlayElement) {
    overlayElement.style.display = '';
  }

  // Restore edit mode class for crosshair cursor
  document.body.classList.add('eventfoundry-edit-mode');
}
